/**
 * Postcode Storage Utilities
 * Manages postcode storage from homepage CTA with configurable expiry
 */

const STORAGE_KEY = 'postcode'

// Get expiry hours from environment variable, default to 1 hour
const getExpiryHours = (): number => {
  const envExpiry = import.meta.env.VITE_POSTCODE_EXPIRY_HOURS
  return envExpiry ? parseFloat(envExpiry) : 1
}

interface PostcodeData {
  postcode: string
  timestamp: string
  expiresAt: string
}

/**
 * Save postcode to localStorage with expiry
 */
export const savePostcode = (postcode: string): void => {
  try {
    const now = new Date()
    const expiryHours = getExpiryHours()
    const expiresAt = new Date(now.getTime() + expiryHours * 60 * 60 * 1000)

    const data: PostcodeData = {
      postcode: postcode.toUpperCase().trim(),
      timestamp: now.toISOString(),
      expiresAt: expiresAt.toISOString()
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch (error) {
    console.error('Error saving postcode to localStorage:', error)
  }
}

/**
 * Load postcode from localStorage, returns null if expired or not found
 */
export const loadPostcode = (): string | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)

    if (!stored) {
      return null
    }

    const data: PostcodeData = JSON.parse(stored)

    // Check if expired
    if (new Date(data.expiresAt) < new Date()) {
      clearPostcode()
      return null
    }

    return data.postcode
  } catch (error) {
    console.error('Error loading postcode from localStorage:', error)
    return null
  }
}

/**
 * Clear postcode from localStorage
 */
export const clearPostcode = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.error('Error clearing postcode from localStorage:', error)
  }
}

/**
 * Check if a valid postcode exists in storage
 */
export const hasPostcode = (): boolean => {
  return loadPostcode() !== null
}

/**
 * Get remaining time before postcode expires (in minutes)
 */
export const getPostcodeTimeRemaining = (): number => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)

    if (!stored) {
      return 0
    }

    const data: PostcodeData = JSON.parse(stored)
    const now = new Date()
    const expiresAt = new Date(data.expiresAt)
    const remainingMs = expiresAt.getTime() - now.getTime()

    return Math.max(0, Math.floor(remainingMs / 60000)) // Convert to minutes
  } catch (error) {
    console.error('Error getting postcode time remaining:', error)
    return 0
  }
}
